import type { TextFilterValue } from '../state/text';
import { TextFilterStateMap } from '../state/text';
import { extractTextCode, isTextParam, prefixText } from './urlPrefixes';
import { Chunk, HashMap, HashSet, Order, pipe, Schema, String as Str, SchemaGetter } from 'effect';

// HashSet to URL string conversion (with encoding removed)
const hashSetToUrlString = (hashSet: HashSet.HashSet<string>): string =>
  pipe(hashSet, Chunk.fromIterable, Chunk.sort(Order.String), Chunk.join('|'));

const urlStringToHashSet = (str: string): HashSet.HashSet<string> =>
  pipe(
    str,
    Str.split('|'),
    Chunk.fromIterable,
    Chunk.filter((s) => s.length > 0),
    HashSet.fromIterable
  );

const textFilterStateMapToUrlRecord = (
  filterStateMap: TextFilterStateMap
): Record<string, string> =>
  HashMap.reduce(filterStateMap, {} as Record<string, string>, (acc, textValue, attributeCode) => {
    if (HashSet.size(textValue) > 0) {
      const key = prefixText(attributeCode);
      acc[key] = hashSetToUrlString(textValue);
    }
    return acc;
  });

const urlRecordToTextFilterStateMap = (urlRecord: Record<string, string>): TextFilterStateMap => {
  let result = HashMap.empty<string, TextFilterValue>();

  for (const [key, value] of Object.entries(urlRecord)) {
    if (!isTextParam(key)) continue;

    const code = extractTextCode(key);
    const hashSetValue = urlStringToHashSet(value);

    result = HashMap.set(result, code, hashSetValue);
  }

  return result;
};

export const UrlQueryRecord = Schema.Record(Schema.String, Schema.String);

export const TextToUrl = Schema.toType(TextFilterStateMap).pipe(
  Schema.decodeTo(UrlQueryRecord, {
    decode: SchemaGetter.transform(textFilterStateMapToUrlRecord),
    encode: SchemaGetter.transform(urlRecordToTextFilterStateMap),
  })
);
